import React, { useEffect } from "react";
import { withTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  InputAdornment,
  Typography,
} from "@mui/material";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import PropTypes from "prop-types";

import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";
import { fireExtinguisherTypeEnum } from "common/enums";

import { onlyNumberKeys } from "utils";

const quantityUnitByFireExtinguisherType = {
  1: "Kg",
  2: "Liters",
  3: "Kg",
  4: "Liters",
  5: "Kg",
  6: "Kg",
  7: "Liters",
  8: "Kg",
};

const FireExtinguisherDetails = ({ formik, t }) => {
  const {
    setFieldValue,
    setFieldTouched,
    values: {
      fireSafetyAddress: { floors: floorOptions },
      fireProtection: { floorWiseFireExtinguishers },
    },
  } = formik;

  const handleAddNewFireExtinguisherDetail = (floorWiseFireExtinguishers) => {
    setFieldValue("fireProtection.floorWiseFireExtinguishers", [
      ...floorWiseFireExtinguishers,
      {
        id: 0,
        floors: [],
        extinguisherType: "",
        capacity: "",
        quantity: "",
      },
    ]);

    setFieldTouched(
      `fireProtection.floorWiseFireExtinguishers[${floorWiseFireExtinguishers.length}]`,
      false,
      false
    );
  };

  const handleRemoveFireExtinguisherDetail = (index) => {
    setFieldValue(
      "fireProtection.floorWiseFireExtinguishers",
      floorWiseFireExtinguishers.filter((_, ind) => ind !== index)
    );
  };

  // creating intital values for fire extinguisher detail
  useEffect(() => {
    if (!floorWiseFireExtinguishers.length && floorOptions.length) {
      setFieldValue("fireProtection.floorWiseFireExtinguishers", [
        {
          id: 0,
          floors: [],
          extinguisherType: "",
          capacity: "",
          quantity: "",
        },
      ]);
    }
  }, [floorOptions]);

  return (
    <>
      <Grid item xs={12} className="border ps-3 pe-2 pb-2 pt-0" sx={{ mb: 3 }}>
        <Grid item xs={12} className="mt-3">
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            <Box>
              <Typography className="mb-3 me-3" lineHeight={1} variant="h6">
                {t("Floor wise Fire Extinguishers Details")}
              </Typography>
            </Box>
            <Box>
              <Button
                color="primary"
                className="mb-3"
                sx={{ minWidth: "unset", textTransform: "capitalize" }}
                variant="outlined"
                onClick={() =>
                  handleAddNewFireExtinguisherDetail(floorWiseFireExtinguishers)
                }
              >
                <strong>
                  <span>+</span> {t("Add Fire Extinguisher Detail")}
                </strong>
              </Button>
            </Box>
          </Box>
        </Grid>

        {!!floorWiseFireExtinguishers.length &&
          floorWiseFireExtinguishers.map(
            ({ floors, extinguisherType, capacity, quantity }, index) => {
              return (
                <Grid
                  key={index}
                  container
                  columnSpacing={1}
                  rowSpacing={1}
                  sx={{ pl: 1 }}
                  className="border border-2 ps-2 pb-0 p-3 mb-3"
                >
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <SelectField
                      label={t("Floor")}
                      name={`fireProtection.floorWiseFireExtinguishers.${index}.floors`}
                      formik={formik}
                      options={floorOptions}
                      multiple
                      disableCloseOnSelect
                      isOptionEqualToValue={(option, { value }) =>
                        option.value === value
                      }
                      renderOption={(props, option, { selected }) => {
                        return (
                          <li {...props} key={option.value}>
                            <Checkbox
                              sx={{ marginRight: "8px" }}
                              checked={selected}
                            />
                            {option.label}
                          </li>
                        );
                      }}
                      onChange={(event, options) => {
                        if (
                          event.key !== "Backspace" &&
                          event.key !== "Delete"
                        ) {
                          setFieldValue(
                            `fireProtection.floorWiseFireExtinguishers.${index}.floors`,
                            options
                          );
                        }
                      }}
                      value={floors || []}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <SelectField
                      label={t("Fire Extinguisher Type")}
                      name={`fireProtection.floorWiseFireExtinguishers.${index}.extinguisherType`}
                      formik={formik}
                      options={fireExtinguisherTypeEnum}
                      disabled={!floors.length}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <InputFieldText
                      label={t("Capacity")}
                      name={`fireProtection.floorWiseFireExtinguishers.${index}.capacity`}
                      type="number"
                      formik={formik}
                      placeholder="Enter Capacity"
                      value={capacity ?? ""}
                      disabled={!extinguisherType}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {
                              quantityUnitByFireExtinguisherType[
                                extinguisherType
                              ]
                            }
                          </InputAdornment>
                        ),
                        inputProps: {
                          min: 0,
                          onKeyDown: (e) => onlyNumberKeys(e, true),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <InputFieldText
                      label={t("Quantity")}
                      name={`fireProtection.floorWiseFireExtinguishers.${index}.quantity`}
                      value={quantity ?? ""}
                      type="number"
                      formik={formik}
                      disabled={!extinguisherType}
                      placeholder="Enter Quantity"
                    />
                  </Grid>
                  {floorWiseFireExtinguishers.length > 1 && (
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Button
                        color="error"
                        size="small"
                        sx={{
                          minWidth: "unset",
                          border: "1px solid",
                          height: "40px",
                          width: "100px",
                          mb: 2,
                        }}
                        onClick={() =>
                          handleRemoveFireExtinguisherDetail(index)
                        }
                      >
                        <strong className="d-flex justify-content-center">
                          <DeleteOutlineSharpIcon fontSize="small" />
                          {t("Remove")}
                        </strong>
                      </Button>
                    </Grid>
                  )}
                </Grid>
              );
            }
          )}
      </Grid>
    </>
  );
};

FireExtinguisherDetails.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(FireExtinguisherDetails);
